import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { formatMatchDate, parseMatchDate } from "@/lib/dates";

const FixturesPage = () => {
  const [matches, setMatches] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCompetition, setSelectedCompetition] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matchesRes, compsRes, teamsRes] = await Promise.all([
          axios.get(`${API}/matches`),
          axios.get(`${API}/competitions`),
          axios.get(`${API}/teams`)
        ]);

        setMatches(matchesRes.data);
        setCompetitions(compsRes.data);

        // Create teams map
        const tMap = {};
        teamsRes.data.forEach(t => { tMap[t.team_id] = t; });
        setTeams(tMap);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => formatMatchDate(dateStr, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const filteredMatches = selectedCompetition === "all" 
    ? matches 
    : matches.filter(m => m.competition_id === selectedCompetition);

  const fixtures = filteredMatches.filter(m => m.status === "scheduled").sort((a, b) => parseMatchDate(a.match_date) - parseMatchDate(b.match_date));
  const results = filteredMatches.filter(m => m.status === "completed").sort((a, b) => parseMatchDate(b.match_date) - parseMatchDate(a.match_date));

  // Group by date
  const groupByDate = (matchList) => {
    const grouped = {};
    matchList.forEach(match => {
      const date = match.match_date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(match);
    });
    return grouped;
  };

  const fixturesByDate = groupByDate(fixtures);
  const resultsByDate = groupByDate(results);

  const getCompetitionName = (compId) => {
    const comp = competitions.find(c => c.competition_id === compId);
    return comp?.short_name || 'Unknown';
  };

  const MatchCard = ({ match, isResult = false }) => (
    <Link
      to={`/matches/${match.match_id}`}
      className={`match-card hover:bg-gray-50 transition-colors ${isResult ? 'border-l-[#009E60]' : ''}`}
      data-testid={`match-card-${match.match_id}`}
    >
      <div className="w-full md:w-auto">
        <span className="text-xs font-bold uppercase text-[#009E60] bg-green-50 px-2 py-1">
          {getCompetitionName(match.competition_id)}
        </span>
      </div>
      <div className="flex items-center gap-4 flex-1 justify-center py-4 md:py-0">
        <div className="flex items-center gap-3">
          <span className="font-heading font-bold text-lg text-right hidden md:block">{teams[match.home_team_id]?.name || 'Home'}</span>
          <span className="font-heading font-bold text-lg md:hidden">{teams[match.home_team_id]?.short_name || 'H'}</span>
          <img 
            src={teams[match.home_team_id]?.logo_url || 'https://placehold.co/48x48/09090b/FFF?text=H'} 
            alt="" 
            className="w-12 h-12 object-contain"
          />
        </div>
        
        {isResult ? (
          <div className="flex flex-col items-center mx-4">
            <div className="flex items-center gap-2 font-heading font-bold text-3xl">
              <span className={match.home_score > match.away_score ? 'text-[#009E60]' : ''}>{match.home_score}</span>
              <span className="text-gray-300">-</span>
              <span className={match.away_score > match.home_score ? 'text-[#009E60]' : ''}>{match.away_score}</span>
            </div>
            {match.shootout_home_score != null && match.shootout_away_score != null && (
              <div className="text-[10px] font-semibold text-[#FFC72C] uppercase tracking-widest mt-0.5" data-testid={`shootout-tag-${match.match_id}`}>
                ({match.shootout_home_score}-{match.shootout_away_score} pens)
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center mx-4">
            <span className="font-heading font-bold text-xl">{match.match_time || 'TBD'}</span>
            <span className="text-xs text-gray-400">VS</span>
          </div>
        )}
        
        <div className="flex items-center gap-3">
          <img 
            src={teams[match.away_team_id]?.logo_url || 'https://placehold.co/48x48/09090b/FFF?text=A'} 
            alt="" 
            className="w-12 h-12 object-contain"
          />
          <span className="font-heading font-bold text-lg text-left hidden md:block">{teams[match.away_team_id]?.name || 'Away'}</span>
          <span className="font-heading font-bold text-lg md:hidden">{teams[match.away_team_id]?.short_name || 'A'}</span>
        </div>
      </div>
      <div className="text-sm text-gray-500 text-center md:text-right w-full md:w-auto">
        {match.stadium || 'TBD'}
      </div>
    </Link>
  );

  return (
    <div data-testid="fixtures-page">
      {/* Hero Section */}
      <section className="bg-[#09090b] py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <Calendar className="w-16 h-16 text-[#FFC72C] mx-auto mb-6" />
          <h1 className="font-heading text-5xl md:text-6xl font-bold uppercase text-white mb-4">
            Fixtures & Results
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Stay up to date with all ABFA match schedules and results.
          </p>
        </div>
      </section>

      {/* Filter & Content */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Competition Filter */}
          <div className="mb-8">
            <Select value={selectedCompetition} onValueChange={setSelectedCompetition}>
              <SelectTrigger className="w-full md:w-64 rounded-none" data-testid="competition-filter">
                <SelectValue placeholder="All Competitions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Competitions</SelectItem>
                {competitions.map(comp => (
                  <SelectItem key={comp.competition_id} value={comp.competition_id}>
                    {comp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Tabs defaultValue="fixtures" className="w-full">
            <TabsList className="w-full justify-start border-b border-gray-200 mb-8 bg-transparent h-auto p-0">
              <TabsTrigger 
                value="fixtures"
                className="px-6 py-4 font-heading font-bold uppercase text-gray-500 data-[state=active]:text-[#09090b] data-[state=active]:border-b-4 data-[state=active]:border-[#FFC72C] rounded-none bg-transparent"
                data-testid="tab-fixtures"
              >
                Upcoming ({fixtures.length})
              </TabsTrigger>
              <TabsTrigger 
                value="results"
                className="px-6 py-4 font-heading font-bold uppercase text-gray-500 data-[state=active]:text-[#09090b] data-[state=active]:border-b-4 data-[state=active]:border-[#FFC72C] rounded-none bg-transparent"
                data-testid="tab-results"
              >
                Results ({results.length})
              </TabsTrigger>
            </TabsList>

            {/* Fixtures Tab */}
            <TabsContent value="fixtures">
              {loading ? (
                <div className="space-y-4">
                  {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 w-full" />)}
                </div>
              ) : fixtures.length > 0 ? (
                <div className="space-y-8">
                  {Object.entries(fixturesByDate).map(([date, dateMatches]) => (
                    <div key={date}>
                      <h3 className="font-heading font-bold text-lg uppercase text-[#09090b] mb-4 border-l-4 border-[#FFC72C] pl-4">
                        {formatDate(date)}
                      </h3>
                      <div className="space-y-4">
                        {dateMatches.map(match => (
                          <MatchCard key={match.match_id} match={match} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-2xl text-gray-400 uppercase mb-2">No Upcoming Fixtures</h3>
                  <p className="text-gray-500">Check back soon for scheduled matches.</p>
                </div>
              )}
            </TabsContent>

            {/* Results Tab */}
            <TabsContent value="results">
              {loading ? (
                <div className="space-y-4">
                  {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 w-full" />)}
                </div>
              ) : results.length > 0 ? (
                <div className="space-y-8">
                  {Object.entries(resultsByDate).map(([date, dateMatches]) => (
                    <div key={date}>
                      <h3 className="font-heading font-bold text-lg uppercase text-[#09090b] mb-4 border-l-4 border-[#009E60] pl-4">
                        {formatDate(date)}
                      </h3>
                      <div className="space-y-4">
                        {dateMatches.map(match => (
                          <MatchCard key={match.match_id} match={match} isResult />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-2xl text-gray-400 uppercase mb-2">No Results Yet</h3>
                  <p className="text-gray-500">Match results will appear here once games are completed.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default FixturesPage;
